/**
 * StickyNewMessage — LinkedIn-style "New message" panel for the sticky widget.
 *
 * Shows a search input and suggested users list. Clicking a user creates a DM
 * conversation and opens it in StickyChatWindow.
 */

import { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import http from '@/network/http';
import { useAuthStore } from '@/features/auth';
import { useCreateConversation, useConversations } from '../hooks';
import { useBatchPresence } from '../hooks';
import { ChatAvatar } from './ChatAvatar';
import { useChatWidgetStore } from '../store';

interface UserResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  avatarUrl?: string | null;
}

// =============================================================================
// User Row
// =============================================================================

interface UserRowProps {
  user: UserResult;
  isOnline: boolean;
  onClick: () => void;
}

const UserRow = memo(function UserRow({ user, isOnline, onClick }: UserRowProps) {
  const { t } = useTranslation();
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-start transition-colors hover:bg-slate-50"
    >
      <div className="relative flex-shrink-0">
        <ChatAvatar src={user.avatarUrl} name={fullName} size="h-10 w-10" />
        {isOnline && (
          <div className="absolute -bottom-0.5 -end-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-success-400" />
        )}
      </div>
      <div className="flex-1 overflow-hidden" dir="auto">
        <div className="truncate text-sm font-semibold" style={{ color: 'var(--user-page-heading)' }}>{fullName}</div>
        <div className="truncate text-xs" style={{ color: 'var(--user-page-text)' }}>
          {t(`roles.${user.role.toLowerCase()}`, user.role)}
        </div>
      </div>
    </button>
  );
});

// =============================================================================
// Main Component
// =============================================================================

export function StickyNewMessage() {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id) || '';
  const { closeNewMessage, openChat } = useChatWidgetStore();

  const [searchQuery, setSearchQuery] = useState('');

  // All users
  const { data: usersData } = useQuery({
    queryKey: ['messaging', 'users'],
    queryFn: () =>
      http.get<{ users: UserResult[] }>('/messaging/users').then((r) => r.data),
    staleTime: 30 * 1000,
  });

  // Existing conversations (to find existing DMs instead of creating duplicates)
  const { data: conversations } = useConversations();

  const allUsers = usersData?.users || [];
  const otherUsers = useMemo(
    () => allUsers.filter((u) => u.id !== currentUserId),
    [allUsers, currentUserId],
  );

  // Batch presence
  const allUserIds = useMemo(() => otherUsers.map((u) => u.id), [otherUsers]);
  const { presenceMap } = useBatchPresence(allUserIds);

  const createConversation = useCreateConversation();

  // Filter users by search
  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const users = q
      ? otherUsers.filter((u) => {
          const fullName = [u.firstName, u.lastName].join(' ').toLowerCase();
          return fullName.includes(q) || u.email.toLowerCase().includes(q);
        })
      : otherUsers;

    // Sort: online first, then alphabetical
    return [...users].sort((a, b) => {
      const aOnline = presenceMap[a.id]?.isOnline ? 1 : 0;
      const bOnline = presenceMap[b.id]?.isOnline ? 1 : 0;
      if (aOnline !== bOnline) return bOnline - aOnline;
      const aName = [a.firstName, a.lastName].join(' ');
      const bName = [b.firstName, b.lastName].join(' ');
      return aName.localeCompare(bName);
    });
  }, [otherUsers, searchQuery, presenceMap]);

  // Click a user — find existing DM or create one, then open chat
  const handleUserClick = useCallback(
    async (user: UserResult) => {
      // Check for existing DM
      const existingDm = conversations?.find(
        (c) =>
          c.type === 'dm' &&
          c.participants.some((p) => p.userId === user.id),
      );

      if (existingDm) {
        openChat(existingDm.id);
        return;
      }

      try {
        const result = await createConversation.mutateAsync({
          type: 'dm',
          participantIds: [user.id],
        });
        openChat(result.conversation.id);
      } catch (error) {
        console.error('Failed to create DM:', error);
      }
    },
    [conversations, createConversation, openChat],
  );

  return (
    <div className="flex max-h-[calc(100dvh-16px)] w-[340px] flex-col overflow-hidden rounded-t-xl border shadow-lg" style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}>
      {/* Header */}
      <div className="flex h-[52px] flex-shrink-0 items-center gap-2 border-b px-4" style={{ borderColor: 'var(--user-page-card-border)' }}>
        <span className="text-sm font-semibold" style={{ color: 'var(--user-page-heading)' }}>{t('messaging.newMessage')}</span>
        <div className="flex-1" />

        {/* Close */}
        <button
          type="button"
          onClick={closeNewMessage}
          className="flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-slate-100"
          style={{ color: 'var(--user-page-text)' }}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Search input */}
      <div className="border-b px-4 py-3" style={{ borderColor: 'var(--user-page-card-border)' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('messaging.widget.searchPeople')}
          dir="auto"
          autoFocus
          className="w-full rounded-full border px-4 py-2 text-sm placeholder:text-slate-400 focus:outline-none"
          style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
        />
      </div>

      {/* Suggested label */}
      <div className="px-4 pt-3 pb-1">
        <span className="text-xs font-medium" style={{ color: 'var(--user-page-text)' }}>{t('messaging.suggestedUsers')}</span>
      </div>

      {/* Users list */}
      <div className="flex-1 overflow-y-auto">
        {filteredUsers.map((user) => (
          <UserRow
            key={user.id}
            user={user}
            isOnline={!!presenceMap[user.id]?.isOnline}
            onClick={() => handleUserClick(user)}
          />
        ))}

        {filteredUsers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-xs" style={{ color: 'var(--user-page-text)' }}>{t('messaging.noUsersFound')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
